// Reactive, client-side view of the auth session.
//
// Wraps the cookie helpers in `session.ts` with Svelte 5 reactive state so any
// component can read `sessionStore.current` / `sessionStore.isAuthenticated`,
// and arms a timer that logs the user out the moment the token expires.
//
// Client-only: never mutated during SSR (all writes go through the browser-
// guarded cookie helpers), so the module-level singleton holds no per-request
// server state.

import { browser } from '$app/environment';
import {
  type Session,
  isSessionValid,
  readSessionCookie,
  writeSessionCookie,
  deleteSessionCookie,
  secondsUntilExpiry
} from './session';

// setTimeout delays are stored as a signed 32-bit int (~24.8 days). Tokens can
// outlive that, so we clamp and re-arm when the clamped timer fires.
const MAX_TIMEOUT = 2_147_483_647;

class SessionStore {
  current = $state<Session | null>(null);

  #timer: ReturnType<typeof setTimeout> | undefined;
  #onExpire: (() => void) | undefined;

  get isAuthenticated(): boolean {
    return isSessionValid(this.current);
  }

  get pubkey(): string | undefined {
    return this.current?.pubkey;
  }

  get token(): string | undefined {
    return this.current?.token;
  }

  // Register what should happen when the token expires (e.g. redirect home).
  onExpire(handler: () => void) {
    this.#onExpire = handler;
  }

  // (Re-)hydrate from the active server's cookie and arm the expiry timer.
  // Called once on the client at startup, and again after an API-server switch
  // — sessions are per-server, so this is what swaps the store over to the new
  // server's token (or empties it when that server has no valid session).
  hydrate() {
    if (!browser) return;

    const session = readSessionCookie();

    if (isSessionValid(session)) {
      this.current = session;
      this.#arm(session);
      return;
    }

    // Expired cookie for this server — clean it up. Either way the store must
    // end up empty: whatever it held belonged to the previous server.
    if (session) deleteSessionCookie();
    this.current = null;
    this.#disarm();
  }

  // Only a valid token for the *currently selected* server counts as signed in.
  set(session: Session) {
    this.current = session;
    writeSessionCookie(session);
    this.#arm(session);
  }

  clear() {
    this.current = null;
    deleteSessionCookie();
    this.#disarm();
  }

  #arm(session: Session) {
    if (!browser) return;

    this.#disarm();
    const delay = Math.min(secondsUntilExpiry(session) * 1000, MAX_TIMEOUT);
    this.#timer = setTimeout(() => this.#onTimer(), delay);
  }

  #disarm() {
    if (this.#timer) {
      clearTimeout(this.#timer);
      this.#timer = undefined;
    }
  }

  #onTimer() {
    // Re-arm if the token outlived a clamped timer and is still valid.
    if (isSessionValid(this.current)) {
      this.#arm(this.current);
      return;
    }

    this.clear();
    this.#onExpire?.();
  }
}

export const sessionStore = new SessionStore();
